import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import MeetingEventBus from '@src/page/toc/common/meeting-room-manager/components/eventBus';
import { PlayerAction, RoomAction } from '../../../message_const';
import { PageChanged } from '@tencent/live-components';
import { MeetingRoomHelper } from '../../../roomEventHandler';
import { openUrlWithCheck, getRedirectUrl } from '@src/js/common/utils';
import { IRemoteControlManager } from '@src/slide/application/service/remote-control-manager/core/interface';

export const enum ErrorType {
    // 账号不同
    account = 'account',
    // 房间已关闭
    roomClose = 'roomClose',
}

export interface IRemoteControlWrapperProps {
    // 分享链接中指定的房间 id
    roomId?: string;
    remoteControlManager: IRemoteControlManager;
    children: React.ReactNode;
    onPlayEnd?: (pageIndex?: number) => void;
    // isFromRemote 标识是否是从房间层来的翻页
    onPrev?: (isFromRemote: boolean) => void;
    onNext?: (isFromRemote: boolean) => void;
    onError?: (errorType: ErrorType) => void;
}

/**
 * 遥控器状态管理基类，包在播放器外面，用于同步和管理遥控器房间状态
 * pc 和 手机 逻辑可能会有差异，差异点可以在子类中写
 */
export default abstract class RemoteControlWrapper<T = unknown> extends Component<IRemoteControlWrapperProps, T> {
    private meetingRoomHelper: MeetingRoomHelper;
    /** 注册事件的销毁函数集合 */
    private eventsOffCallbacks: Array<() => void> = [];
    // loading 相关状态
    private loadingBar: ReturnType<typeof Snackbar.show>;
    private loadingPromiseResolve: (value?: unknown) => void;
    private loadingPromise = new Promise(resolve => this.loadingPromiseResolve = resolve);

    public constructor(props: IRemoteControlWrapperProps) {
        super(props);
        this.initRemoteControlManager();
    }

    public render(): React.ReactNode {
        return (
            <>
                {this.props.children}
            </>
        );
    }

    public componentWillUnmount(): void {
        this.props.remoteControlManager.event.off(PageChanged, this.onMeetingRoomPageChanged);
        this.eventsOffCallbacks.forEach(unbind => unbind());
        this.hideLoadingBar();
    }

    /**
     * 等待 loading 完成
     */
    protected async afterLoading() {
        await this.loadingPromise;
    }

    private initRemoteControlManager() {
        this.meetingRoomHelper = MeetingRoomHelper.getInstance();
        this.props.remoteControlManager.setMeetingRoomHelper(this.meetingRoomHelper);
        this.initRoom();
        this.registerEventHandler();
    }

    /**
     * 添加房间层等事件监听
     */
    private registerEventHandler = () => {
        this.eventsOffCallbacks.push(
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.PlayerReady, () => {
                MeetingEventBus.sendRoomEvent2Player(RoomAction.ShowPlayerContent, {
                    currentPage: 0,
                    currentRev: this.props.remoteControlManager.liveInfo.revision,
                });
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.GotoPageFragment, (data) => {
                this.meetingRoomHelper.playerState = {
                    playerAction: PlayerAction.GotoPageFragment,
                    ...data,
                };
                this.handlePageChange({
                    isLoadPageBySwitchForward: data.isLoadPageBySwitchForward, isFromRemote: false,
                });
                // 猜测是为了解决一些浏览器的安全限制导致一些自动行为无法触发的问题
                document.body.click();
                this.props.remoteControlManager.gotoPage();
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.EndPlay, (data) => {
                this.props.onPlayEnd?.(data.pageIndex);
                this.props.remoteControlManager.liveEnd();
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.LinkClicked, ({ href }) => {
                openUrlWithCheck(getRedirectUrl(href), true);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.DataLoading, ({ loadingText }) => {
                this.showLoadingBar(loadingText);
            }),
            MeetingEventBus.onRecvPlayerEvent(PlayerAction.CancelDataLoading, () => {
                this.hideLoadingBar();
            }),
        );


        this.props.remoteControlManager.event.on(PageChanged, this.onMeetingRoomPageChanged);
    };

    /**
     * 响应房间层来的翻页
     */
    private onMeetingRoomPageChanged = () => {
        const serverData = this.props.remoteControlManager.targetPosition();
        const localData = MeetingRoomHelper.getInstance().playerState;
        const { currentPage, currentFragment, iaFragmentInfo, shouldStopMainAnim } = localData;
        const serverStopA = (serverData as any).stopA;
        if (
            serverData.p !== currentPage
            || serverData.f !== currentFragment
            || !isEqual(iaFragmentInfo, serverData.iaF)
            || serverStopA !== shouldStopMainAnim
        ) {
            const isLoadPageBySwitchForward = !!(serverData as any).prev;
            MeetingEventBus.sendRoomEvent2Player(RoomAction.RemoteChangePage, {
                currentPage: serverData.p,
                currentFragment: serverData.f,
                iaFragmentInfo: serverData.iaF,
                shouldStopMainAnim: serverStopA,
                isLoadPageBySwitchForward,
            });
            this.handlePageChange({ isLoadPageBySwitchForward, isFromRemote: true });
        }
    };


    private handlePageChange = ({ isLoadPageBySwitchForward, isFromRemote }) => {
        if (isLoadPageBySwitchForward) {
            this.props.onPrev?.(isFromRemote);
        } else {
            this.props.onNext?.(isFromRemote);
        }
    };

    private showLoadingBar(text: string) {
        this.loadingBar = Snackbar.show({
            message: text,
            type: 'loading',
            autoClose: false,
        });
    }

    private hideLoadingBar = () => {
        this.loadingBar?.close();
        this.loadingPromiseResolve();
    };

    /**
     * 初始化房间相关逻辑，涉及到错误状态判断，子类实现
     */
    protected abstract initRoom(): void;
}
